/**
 * Trophy Room - Display and feature achievements
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { http } from '../../api/http';
import { usePlayerStatsStore } from '../../state/playerStatsStore';

interface Achievement {
  id: string;
  name: string;
  description: string;
  xpReward: number;
}

const ACHIEVEMENT_ICONS: Record<string, string> = {
  first_win: '🏆',
  streak_3: '🔥',
  streak_5: '💥',
  streak_10: '👑',
  reach_silver: '🥈',
  reach_gold: '🥇',
  reach_platinum: '💎',
  reach_diamond: '💠',
  reach_champion: '🏅',
  games_10: '🎮',
  games_50: '🎯',
  games_100: '⭐',
  underdog: '🦁',
  level_10: '📈',
  level_25: '📊',
  level_50: '🚀',
};

export const TrophyRoomPage: React.FC = () => {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const stats = usePlayerStatsStore((s) => s.stats);
  const unlockedIds = stats?.achievements ?? [];

  useEffect(() => {
    if (!wallet) {
      navigate('/');
      return;
    }

    const fetchAchievements = async () => {
      try {
        const response = await http.get<{ achievements: Achievement[] }>('/achievements');
        setAllAchievements(response.achievements);

        // Load featured from localStorage
        const stored = localStorage.getItem(`featured-achievements-${wallet}`);
        if (stored) {
          setFeaturedIds(JSON.parse(stored));
        }
      } catch (error) {
        console.error('Failed to fetch achievements:', error);
        toast.error('Failed to load achievements');
      } finally {
        setLoading(false);
      }
    };

    fetchAchievements();
  }, [wallet, navigate]);

  const toggleFeatured = (achievementId: string) => {
    if (!unlockedIds.includes(achievementId)) {
      toast.error('Unlock this achievement first!');
      return;
    }

    setFeaturedIds((prev) => {
      let newFeatured: string[];
      if (prev.includes(achievementId)) {
        // Remove from featured
        newFeatured = prev.filter((id) => id !== achievementId);
      } else if (prev.length < 3) {
        // Add to featured
        newFeatured = [...prev, achievementId];
      } else {
        toast.error('You can only feature 3 achievements');
        return prev;
      }

      // Persist to localStorage
      localStorage.setItem(`featured-achievements-${wallet}`, JSON.stringify(newFeatured));
      return newFeatured;
    });
  };

  if (!wallet) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-white/60">Loading achievements...</div>
      </div>
    );
  }

  const unlockedCount = unlockedIds.length;
  const totalCount = allAchievements.length;
  const totalXPEarned = allAchievements
    .filter((a) => unlockedIds.includes(a.id))
    .reduce((sum, a) => sum + a.xpReward, 0);

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white transition"
          >
            &larr; Back
          </button>
          <h1 className="font-display text-2xl font-bold neon-text">Trophy Room</h1>
          <div className="w-16" />
        </div>

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-3 gap-4"
        >
          <div className="arcade-panel p-4 text-center">
            <p className="text-3xl font-bold text-neon-400">{unlockedCount}</p>
            <p className="text-xs text-white/50 uppercase tracking-wide">Unlocked</p>
          </div>
          <div className="arcade-panel p-4 text-center">
            <p className="text-3xl font-bold text-white">{totalCount}</p>
            <p className="text-xs text-white/50 uppercase tracking-wide">Total</p>
          </div>
          <div className="arcade-panel p-4 text-center">
            <p className="text-3xl font-bold text-emerald-400">{totalXPEarned}</p>
            <p className="text-xs text-white/50 uppercase tracking-wide">XP Earned</p>
          </div>
        </motion.div>

        {/* Featured Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="arcade-panel p-4"
        >
          <h2 className="text-sm uppercase tracking-widest text-white/50 mb-3">Featured Badges</h2>
          <p className="text-xs text-white/40 mb-4">
            Select up to 3 achievements to display on your profile
          </p>
          <div className="flex gap-3 justify-center">
            {featuredIds.length === 0 ? (
              <p className="text-white/40 text-sm">Click achievements below to feature them</p>
            ) : (
              featuredIds.map((id) => {
                const achievement = allAchievements.find((a) => a.id === id);
                if (!achievement) return null;
                return (
                  <motion.div
                    key={id}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl bg-neon-500/10 border border-neon-400/30"
                  >
                    <span className="text-3xl">{ACHIEVEMENT_ICONS[id] ?? '🏅'}</span>
                    <span className="text-xs font-semibold text-neon-400">{achievement.name}</span>
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>

        {/* All Achievements Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="arcade-panel p-4"
        >
          <h2 className="text-sm uppercase tracking-widest text-white/50 mb-4">All Achievements</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {allAchievements.map((achievement) => {
              const isUnlocked = unlockedIds.includes(achievement.id);
              const isFeatured = featuredIds.includes(achievement.id);

              return (
                <motion.button
                  key={achievement.id}
                  type="button"
                  onClick={() => toggleFeatured(achievement.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`relative p-4 rounded-xl border transition ${
                    isUnlocked
                      ? isFeatured
                        ? 'border-neon-400/50 bg-neon-500/10'
                        : 'border-white/20 bg-white/5 hover:bg-white/10'
                      : 'border-white/10 bg-black/30 opacity-50 cursor-not-allowed'
                  }`}
                >
                  {/* Featured indicator */}
                  {isFeatured && (
                    <span className="absolute top-1 right-1 text-xs text-neon-400">★</span>
                  )}

                  {/* Icon */}
                  <div className={`text-4xl mb-2 ${isUnlocked ? '' : 'grayscale'}`}>
                    {ACHIEVEMENT_ICONS[achievement.id] ?? '🏅'}
                  </div>

                  {/* Name */}
                  <h3
                    className={`font-semibold text-sm ${
                      isUnlocked ? 'text-white' : 'text-white/50'
                    }`}
                  >
                    {achievement.name}
                  </h3>

                  {/* Description */}
                  <p className="text-[10px] text-white/50 mt-1">{achievement.description}</p>

                  {/* XP Reward */}
                  <div
                    className={`mt-2 text-[10px] font-semibold ${
                      isUnlocked ? 'text-emerald-400' : 'text-white/30'
                    }`}
                  >
                    +{achievement.xpReward} XP
                  </div>

                  {/* Lock indicator */}
                  {!isUnlocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
                      <span className="text-2xl">🔒</span>
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
